import type { ThemeColors, ThemeShadows } from "@smartrent/tokens";
import type { ViewStyle } from "react-native";
import type { ButtonColors, ButtonVariation } from "./types";

export type GetButtonTextColorStyles = ({
  variation,
  color,
  disabled,
  colors,
  isLightMode,
  isLoading,
}: {
  variation: ButtonVariation;
  color: ButtonColors;
  disabled: boolean;
  colors: ThemeColors;
  isLightMode: boolean;
  isLoading?: boolean;
}) => {
  normal: { color: string };
  // used for pressed and hover states
  active?: { color: string };
};

export type GetButtonIconColorStyles = ({
  variation,
  color,
  disabled,
  colors,
  isLightMode,
  isLoading,
}: {
  variation: ButtonVariation;
  color: ButtonColors;
  disabled: boolean;
  colors: ThemeColors;
  isLightMode: boolean;
  isLoading?: boolean;
}) => {
  normal: { color: string };
  // used for pressed and hover states
  active?: { color: string };
};

export const getButtonIconColorStyles: GetButtonIconColorStyles = ({
  variation,
  color,
  disabled,
  colors,
  isLightMode,
  isLoading,
}) => {
  if (isLoading || disabled) {
    return {
      normal: {
        color: colors.buttonIconDisabled,
      },
    };
  }

  switch (variation) {
    case "outlined":
    case "muted":
    case "plain":
      switch (color) {
        case "destructive":
          return {
            normal: {
              color: colors.buttonIconDestructiveInverse,
            },
            active: {
              color: colors.buttonIconDestructiveInverseHover,
            },
          };
        // color="primary"
        default:
          return {
            normal: {
              color: colors.buttonIconInverse,
            },
            active: {
              color: colors.buttonIconInverseHover,
            },
          };
      }
    // variation="default"
    default:
      switch (color) {
        case "destructive":
          return {
            normal: {
              color: colors.buttonIconDestructive,
            },
            active: {
              color: colors.buttonIconDestructiveHover,
            },
          };
        default:
          return {
            normal: {
              color: colors.buttonIconPrimary,
            },
            active: {
              color: colors.buttonIconPrimaryHover,
            },
          };
      }
  }
};

export const getButtonTextColorStyles: GetButtonTextColorStyles = ({
  variation,
  color,
  disabled,
  colors,
  isLightMode,
  isLoading,
}) => {
  if (isLoading || disabled) {
    return {
      normal: {
        color: colors.buttonTextDisabled,
      },
      active: {
        color: colors.buttonTextDisabled,
      },
    };
  }

  switch (variation) {
    case "muted":
    case "outlined":
    case "plain":
      switch (color) {
        case "destructive":
          return {
            normal: {
              color: colors.buttonTextDestructiveInverse,
            },
            active: {
              color: colors.buttonTextDestructiveInverseHover,
            },
          };
        // color="primary"
        default:
          return {
            normal: {
              color: colors.buttonTextPrimaryInverse,
            },
            active: {
              color: colors.buttonTextPrimaryInverseHover,
            },
          };
      }
    // variation="default"
    default:
      switch (color) {
        case "destructive":
          return {
            normal: {
              color: colors.buttonTextDestructive,
            },
            active: {
              color: colors.buttonTextDestructiveHover,
            },
          };
        // color="primary"
        default:
          return {
            normal: {
              color: colors.buttonTextPrimary,
            },
            active: {
              color: colors.buttonTextPrimaryHover,
            },
          };
      }
  }
};

export type GetButtonStyles = ({
  floating,
  variation,
  color,
  disabled,
  colors,
  shadows,
  isLoading,
  isLightMode,
}: {
  floating?: boolean;
  variation: ButtonVariation;
  color: ButtonColors;
  disabled: boolean;
  colors: ThemeColors;
  shadows: ThemeShadows;
  isLoading?: boolean;
  isLightMode?: boolean;
}) => {
  normal: ViewStyle;
  // used for pressed and hover states
  active?: ViewStyle;
};

export const getButtonStyles: GetButtonStyles = ({
  floating = false,
  variation,
  color,
  disabled,
  colors,
  shadows,
  isLoading,
  isLightMode,
}) => {
  if (isLoading) {
    switch (variation) {
      case "plain":
        return {
          normal: {
            backgroundColor: "transparent",
            borderWidth: 0,
          },
          active: {
            backgroundColor: "transparent",
            borderWidth: 0,
          },
        };
      case "outlined":
        return {
          normal: {
            borderColor: colors.buttonBackgroundDisabled,
            borderWidth: 2,
          },
          active: {
            borderColor: colors.buttonBackgroundDisabled,
            borderWidth: 2,
          },
        };

      default:
        return {
          normal: {
            backgroundColor: colors.buttonBackgroundDisabled,
            borderWidth: 0,
          },
          active: {
            backgroundColor: colors.buttonBackgroundDisabled,
            borderWidth: 0,
          },
        };
    }
  }

  switch (variation) {
    case "outlined": {
      if (disabled) {
        return {
          normal: {
            backgroundColor: "transparent",
            borderWidth: 2,
            borderColor: colors.buttonStrokeDisabled,
          },
        };
      }

      switch (color) {
        case "destructive":
          return {
            normal: {
              backgroundColor: "transparent",
              borderWidth: 2,
              borderColor: colors.buttonStrokeDestructive,
            },
            active: {
              backgroundColor: "transparent",
              borderWidth: 2,
              borderColor: colors.buttonStrokeDestructiveHover,
            },
          };
        // color="primary"
        default:
          return {
            normal: {
              backgroundColor: "transparent",
              borderWidth: 2,
              borderColor: colors.buttonStrokePrimary,
            },
            active: {
              backgroundColor: "transparent",
              borderWidth: 2,
              borderColor: colors.buttonStrokePrimaryHover,
            },
          };
      }
    }
    case "plain": {
      // disabled and colors don't apply as the background/border will always be transparent
      return {
        normal: {
          backgroundColor: "transparent",
          borderWidth: 0,
        },
      };
    }
    case "muted": {
      if (disabled) {
        return {
          normal: {
            backgroundColor: colors.buttonBackgroundDisabled,
            borderWidth: 0,
          },
        };
      }

      return {
        normal: {
          borderWidth: 0,
          backgroundColor: colors.buttonBackgroundMuted,
        },
        active: {
          // text will change colors but not background color
          backgroundColor: colors.buttonBackgroundMutedHover,
        },
      };
    }
    // variation="default"
    default: {
      if (disabled) {
        return {
          normal: {
            backgroundColor: colors.buttonBackgroundDisabled,
            borderWidth: 0,
          },
        };
      }

      switch (color) {
        case "destructive":
          return {
            normal: {
              borderWidth: 0,
              backgroundColor: colors.buttonBackgroundDestructive,
              ...shadows.button,
            },
            active: {
              backgroundColor: colors.buttonBackgroundDestructiveHover,
              ...shadows.buttonHover,
            },
          };
        // color="primary"
        default:
          return {
            normal: {
              borderWidth: 0,
              backgroundColor: colors.buttonBackgroundPrimary,
              ...shadows.button,
            },
            active: {
              backgroundColor: colors.buttonBackgroundPrimaryHover,
              ...shadows.buttonHover,
            },
          };
      }
    }
  }
};
